import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, isRef as _isRef, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex space-x-4" }
const _hoisted_2 = { class: "w-full space-x-2 flex justify-end" }
const _hoisted_3 = { class: "flex space-x-4" }

import { useModalsStore } from '@/stores/modals';
import PageWrapper from '@components/page-wrapper/PageWrapper.vue';
import Button from '@ui-kit/controls/button/Button.vue';
import ModalManageOrder from './modals/manage-order/ModalManageOrder.vue';
import { Table, TableComponentParams, TableServerDataManager } from '@ui-kit/table';
import { RepairOrderClosedResource, RepairOrderResource } from '@shared/resources/repair-order';
import { computed, ref, watch } from 'vue';
import { timestampToDate } from '@/helpers/datetime';
import { useI18n } from 'vue-i18n';
import { getClientName } from '@/helpers/clients';
import { Filters } from '@ui-kit/table/types';
import { TabData, Tabs } from '@ui-kit/tabs';
import { isRepair, OrderType, OrderTypeList, OrderTypeNameMap } from '@shared/types/order-type';
import { SingleSelect, useSelectClientDataManager } from '@ui-kit/inputs/select';
import { OrderService } from '@/services/order.service';
import { ModalConfirmation } from '@components/modals';
import { coinsFormat } from '@shared/helpers/data-converters/number-converters';
import ClientSelect from '../../partials/client-select/ClientSelect.vue';
import { ClientResource } from '@shared/resources/client';
import SelectPrintNew from './SelectPrintNew.vue';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { getClientCarName } from '@/helpers/cars';
import { SortingOrder } from '@shared/types/sorting-order';
import OrderTablePreviewNew from './order-table-preview-new/OrderTablePreviewNew.vue';
import { START_DOCUMENT_NUMBER } from '@components/print/constants';

interface OrderTypeOption {
  id: OrderType;
  name: string;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'PageOrders',
  setup(__props) {

const modalsStore = useModalsStore();
const { t } = useI18n();
const orderService = new OrderService();

const orderTypeOptions = computed<OrderTypeOption[]>(() => {
  return OrderTypeList.map((id) => ({
    id,
    name: t(`common.order_type.${OrderTypeNameMap[id]}`),
  }));
});
const orderType = useLocalStorage<OrderTypeOption>('PAGE_ORDERS_CURRENT_TYPE', orderTypeOptions.value[2]);
const orderSelectManager = useSelectClientDataManager({
  selected: orderType,
  hideSelected: true,
  data: orderTypeOptions,
});

const selectedClient = ref<ClientResource>();

const currentTab = useLocalStorage<string>('PAGE_ORDERS_CURRENT_TAB', 'open');
const tabs = computed<TabData[]>(() => {
  return [
    {
      name: 'open',
      title: 'Відкриті',
    },
    {
      name: 'closed',
      title: 'Закриті',
    },
  ];
});

const tableParams = computed<TableComponentParams<RepairOrderResource>>(() => {
  return {
    columns: [
      {
        title: '№',
        name: 'number',
        width: 75,
        data: (row) => START_DOCUMENT_NUMBER + row.id,
        responsiveOrder: 0,
      },
      {
        title: t('fields.date'),
        name: 'date',
        width: 120,
        data: (row) => row.date ? timestampToDate(row.date) : '-',
        responsiveOrder: 1,
      },
      {
        title: t('fields.type'),
        name: 'type',
        width: 200,
        data: (row) => t(`common.order_type.${OrderTypeNameMap[row.type]}`),
        responsiveOrder: 2,
      },
      {
        title: t('fields.client'),
        name: 'client.name',
        data: (row) => row.client ? getClientName(row.client) : '-',
        responsiveOrder: 3,
      },
      {
        title: t('fields.car'),
        name: 'car',
        hidden: !isRepair(orderType.value.id),
        data: (row) => getClientCarName(row.car),
      },
      {
        title: t('fields.sum_local'),
        name: 'sum',
        width: 180,
        data: (row) => coinsFormat(row.sum),
      },
    ],
  };
});
const newNonDraftTableParams = computed<TableComponentParams<RepairOrderClosedResource>>(() => {
  return {
    expandable: true,
    columns: [
      {
        title: '№',
        name: 'number',
        width: 75,
        data: (row) => START_DOCUMENT_NUMBER + row.id,
        responsiveOrder: 0,
      },
      {
        title: t('fields.date'),
        name: 'date',
        width: 120,
        data: (row) => row.date ? timestampToDate(row.date) : '-',
        responsiveOrder: 1,
      },
      {
        title: t('fields.type'),
        name: 'type',
        width: 200,
        data: (row) => t(`common.order_type.${OrderTypeNameMap[row.orderType]}`),
        responsiveOrder: 2,
      },
      {
        title: t('fields.client'),
        name: 'client.name',
        data: (row) => row.client ? row.client.name : '-',
        responsiveOrder: 3,
      },
      {
        title: t('fields.car'),
        name: 'car',
        hidden: !isRepair(orderType.value.id),
        data: (row) => row.carName ? row.carName : '-',
      },
      {
        title: t('fields.sum_local'),
        name: 'sum',
        width: 180,
        data: (row) => coinsFormat(row.sum),
      },
      {
        name: 'actions',
        width: 350,
        title: '',
        responsiveOrder: 4,
      },
    ],
  };
});
const draftTableParams = computed<TableComponentParams<RepairOrderResource>>(() => {
  return {
    columns: [
      ...tableParams.value.columns,
      {
        name: 'actions',
        width: 200,
        title: '',
        responsiveOrder: 4,
      },
    ]
  }
})
const newTableManager = new TableServerDataManager({
  path: 'orders/closed/grid',
});
const draftTableManager = new TableServerDataManager({
  path: 'orders/grid',
  initialSortOrder: {
    column: 'date',
    order: SortingOrder.DESC,
  },
});
const typeFilter = computed(() => {
  switch (orderType.value.id) {
    case OrderType.ORDER: return [OrderType.ORDER, OrderType.REPAIR_ORDER];
    case OrderType.REPAIR: return [OrderType.REPAIR, OrderType.REPAIR_ORDER];
  }
  return undefined;
});
const draftTableFilters = computed<Filters>(() => {
  return {
    is_draft: 1,
    type: typeFilter.value,
    client_id: selectedClient.value?.id || undefined,
  };
});
const tableFilters = computed<Filters>(() => {
  return {
    type: typeFilter.value,
    client_id: selectedClient.value?.id || undefined,
  };
});
watch(draftTableFilters, (value) => {
  draftTableManager.setFilters(value);
}, { immediate: true });
watch(tableFilters, (value) => {
  newTableManager.setFilters(value);
}, { immediate: true });

function removeOrder(order: RepairOrderResource) {
  modalsStore.addModal({
    component: ModalConfirmation,
    props: {
      title: t('pages.orders.remove-label'),
      danger: true,
      confirmText: t('common.actions.delete'),
      confirm: () => {
        orderService.remove(order.id).send().then(() => {
          updateDraftOrders();
        });
      },
    },
  });
}
async function manageOrder(order?: RepairOrderResource) {
  modalsStore.addModal({
    component: ModalManageOrder,
    events: {
      update() {
        updateDraftOrders();
      },
      close() {
        updateTables();
      },
    },
    props: {
      data: order,
    },
  });
}

function updateTables() {
  updateDraftOrders();
  updateClosedOrders();
}
function updateDraftOrders() {
  draftTableManager.request();
}
function updateClosedOrders() {
  newTableManager.request();
}

return (_ctx: any,_cache: any) => {
  const _component_FontAwesomeIcon = _resolveComponent("FontAwesomeIcon")!

  return (_openBlock(), _createBlock(PageWrapper, {
    title: _ctx.$t('navigation.orders')
  }, {
    default: _withCtx(() => [
      _createVNode(_unref(Tabs), {
        tabs: _unref(tabs),
        "current-tab": _unref(currentTab),
        "onUpdate:current-tab": _cache[5] || (_cache[5] = ($event: any) => (_isRef(currentTab) ? (currentTab).value = $event : null))
      }, {
        "tabs-end": _withCtx(() => [
          _createVNode(Button, {
            size: "lg",
            class: "ml-4",
            "button-type": "outline",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (manageOrder()))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_FontAwesomeIcon, {
                icon: "plus",
                class: "mr-2"
              }),
              _createTextVNode(_toDisplayString(_ctx.$t('common.actions.create_new')), 1)
            ]),
            _: 1
          })
        ]),
        "tab-open": _withCtx(() => [
          _createVNode(_unref(Table), {
            "data-manager": _unref(draftTableManager),
            params: _unref(draftTableParams)
          }, {
            "header-left": _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_unref(SingleSelect), {
                  class: "w-64",
                  "data-manager": _unref(orderSelectManager),
                  modelValue: _unref(orderType),
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(orderType) ? (orderType).value = $event : null)),
                  "hide-empty": "",
                  "disable-search": ""
                }, null, 8, ["data-manager", "modelValue"]),
                _createVNode(ClientSelect, {
                  class: "w-64",
                  modelValue: selectedClient.value,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedClient).value = $event))
                }, null, 8, ["modelValue"])
              ])
            ]),
            "cell-actions": _withCtx(({ row }) => [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(Button, {
                  size: "sm",
                  onClick: ($event: any) => (manageOrder(row))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_FontAwesomeIcon, {
                      icon: "pen",
                      class: "mr-2"
                    }),
                    _createTextVNode(_toDisplayString(_ctx.$t('common.actions.edit')), 1)
                  ]),
                  _: 2
                }, 1032, ["onClick"]),
                _createVNode(Button, {
                  size: "sm",
                  color: "danger",
                  onClick: ($event: any) => (removeOrder(row))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_FontAwesomeIcon, { icon: "trash" })
                  ]),
                  _: 2
                }, 1032, ["onClick"])
              ])
            ]),
            _: 1
          }, 8, ["data-manager", "params"])
        ]),
        "tab-closed": _withCtx(() => [
          _createVNode(_unref(Table), {
            "data-manager": _unref(newTableManager),
            params: _unref(newNonDraftTableParams)
          }, {
            "header-left": _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_unref(SingleSelect), {
                  class: "w-64",
                  "data-manager": _unref(orderSelectManager),
                  modelValue: _unref(orderType),
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_isRef(orderType) ? (orderType).value = $event : null)),
                  "hide-empty": "",
                  "disable-search": ""
                }, null, 8, ["data-manager", "modelValue"]),
                _createVNode(ClientSelect, {
                  class: "w-64",
                  modelValue: selectedClient.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((selectedClient).value = $event))
                }, null, 8, ["modelValue"])
              ])
            ]),
            "cell-actions": _withCtx(({row}) => [
              _createVNode(SelectPrintNew, {
                data: row,
                class: "w-full"
              }, null, 8, ["data"])
            ]),
            expanded: _withCtx(({row}) => [
              _createVNode(OrderTablePreviewNew, {
                onUpdate: updateClosedOrders,
                data: row
              }, null, 8, ["data"])
            ]),
            _: 1
          }, 8, ["data-manager", "params"])
        ]),
        _: 1
      }, 8, ["tabs", "current-tab"])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})